import React, { useContext, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useHistory, useLocation } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { parse, stringify } from '../../utils/UrlUtil';
import { AccountContext } from '../../components/AccountContext';
import useSubscriptionExpiredRestrictions from '../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  all: { id: 'all', defaultMessage: 'ALL' },
  new: { id: 'candidate.status.NEW', defaultMessage: 'NEW' },
  invited: { id: 'candidate.status.INVITED', defaultMessage: 'INVITED' },
  started: { id: 'candidate.status.STARTED', defaultMessage: 'IN PROGRESS' },
  declined: { id: 'candidate.status.DECLINED', defaultMessage: 'DECLINED' },
  expired: { id: 'candidate.status.EXPIRED', defaultMessage: 'EXPIRED' },
  submitted: { id: 'candidate.status.SUBMITTED', defaultMessage: 'ASSESSED' },
  addNewAssessment: { id: 'btn.label.addNewAssessment', defaultMessage: 'Add New Assessment' },
});

const STATUS_BUTTON = [
  {
    filter: 'ALL',
  },
  {
    filter: 'NEW',
  },
  {
    filter: 'INVITED',
  },
  {
    filter: 'STARTED',
  },
  {
    filter: 'SUBMITTED',
  },
  {
    filter: 'DECLINED',
  },
  {
    filter: 'EXPIRED',
  },
];

const buildStatusFilter = (status, formatMessage) => {
  switch (status) {
    case 'ALL': {
      return {
        text: formatMessage(messages.all),
      };
    }
    case 'NEW': {
      return {
        text: formatMessage(messages.new),
      };
    }
    case 'INVITED': {
      return {
        text: formatMessage(messages.invited),
      };
    }
    case 'STARTED': {
      return {
        text: formatMessage(messages.started),
      };
    }
    case 'DECLINED': {
      return {
        text: formatMessage(messages.declined),
      };
    }
    case 'EXPIRED': {
      return {
        text: formatMessage(messages.expired),
      };
    }
    case 'SUBMITTED': {
      return {
        text: formatMessage(messages.submitted),
      };
    }
    default: {
      return {};
    }
  }
};

const statusButtonFilters = ({ onFilter, currentStatus, formatMessage }) =>
  STATUS_BUTTON.map((status) => {
    const statusProps = buildStatusFilter(status.filter, formatMessage);
    const { text } = statusProps;

    const buttonStyle = currentStatus === status.filter ? 'p-button-info' : 'p-button-secondary';

    return (
      <Button
        key={`filter-${status.filter}`}
        label={text}
        className={buttonStyle}
        onClick={() => onFilter(status.filter)}
      />
    );
  });

const AssessmentListTableHeader = () => {
  const currentAccount = useContext(AccountContext);
  const { formatMessage } = useIntl();
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();
  const history = useHistory();
  const location = useLocation();
  const params = parse(location.search);

  const [filter, setFilter] = useState({ ...params.filter });

  /**
   * Get old URL and:
   * - parse query params
   * - merge new param values
   * - stringify query params
   * - push new url
   */
  const onQueryVariableChange = (filter) => {
    const params = parse(location.search);

    // build the filter
    const newFilter = {
      ...params.filter,
      ...filter,
    };

    // handle edge case for respondentType = ALL.
    if (newFilter.status === 'ALL') {
      newFilter.status = undefined;
    }

    const search = stringify({
      ...params,
      filter: newFilter,
    });

    // update url with the new search, keep the rest of the values
    history.push({
      ...location,
      search,
    });
  };

  /**
   * Update the filter Query.
   */
  const onChangeQueryText = (event) => {
    const updatedFilter = {
      ...filter,
      query: event.currentTarget.value,
    };

    setFilter(updatedFilter);
  };

  const onStatusFilterChange = (status) => {
    const updatedFilter = {
      ...filter,
      status,
    };

    setFilter(updatedFilter);
    onQueryVariableChange(updatedFilter);
  };

  const { status } = filter;
  const statusFilter = statusButtonFilters({
    onFilter: onStatusFilterChange,
    currentStatus: status,
    formatMessage,
  });
  return (
    <div className="p-grid">
      <div className="p-col p-sm-6">
        <div className="p-inputgroup">
          <InputText
            value={filter.query}
            onChange={onChangeQueryText}
            onKeyPress={(event) => {
              if (event.key === 'Enter') {
                onQueryVariableChange(filter);
              }
            }}
          />
          <Button icon="pi pi-search" onClick={() => onQueryVariableChange(filter)} />
        </div>
      </div>
      <div className="p-col p-sm-6 align-right">
        <Button
          label={formatMessage(messages.addNewAssessment)}
          disabled={subscriptionIsExpired}
          onClick={() => history.push(`/accounts/${currentAccount}/respondents`)}
        />
      </div>
      <div className="p-col-12">
        <div className="status-filter">{statusFilter}</div>
      </div>
    </div>
  );
};

export default AssessmentListTableHeader;
